import axios from "axios";
import { env, isProd, isMockApi } from "@/config/env";
import { initSentry } from "@/observability/sentry";
import { logger } from "@/observability/logger";
import { useAuthStore } from "@/store/auth-store";
import type { User } from "@/api/types";
import {
  refreshAccessToken,
  setAuthToken,
  setRefreshToken,
} from "@/api/client";

export async function bootstrap(): Promise<void> {
  if (isProd && isMockApi) {
    throw new Error(
      "FATAL: MSW cannot be enabled in production! " +
        "Set VITE_IS_MOCK_API=false for production builds.",
    );
  }

  logger.info("Starting application bootstrap", {
    mode: import.meta.env.MODE,
    isMockApi: env.VITE_IS_MOCK_API,
    version: env.VITE_APP_VERSION,
  });

  initSentry();

  if (isMockApi && !isProd) {
    logger.info("Starting MSW in mock mode");
    try {
      const { startMsw } = await import("@/mocks/bootstrap");
      await startMsw();
    } catch (error) {
      logger.error("Failed to start MSW", { error });
      throw error;
    }
  }

  // Restore access token for reloaded sessions.
  // accessToken is not persisted (XSS risk); expiry being absent signals a
  // reload. We block rendering here so the app never starts without a valid
  // token — MSW must be running first so mock mode intercepts this call too.
  const storedUser = useAuthStore.getState().user;
  if (storedUser?.refreshToken && !storedUser.expiry) {
    logger.info("Restoring session from refresh token");
    try {
      const refreshed = await refreshAccessToken(storedUser.refreshToken);
      setAuthToken(refreshed.accessToken);
      setRefreshToken(refreshed.refreshToken);
      useAuthStore.getState().setUser({ ...storedUser, ...refreshed } as User);
      logger.info("Session restored");
    } catch (error) {
      const status = axios.isAxiosError(error) ? error.response?.status : null;
      if (status === 401 || status === 403) {
        // Refresh token definitively rejected by the server — force re-login.
        logger.info("Refresh token rejected — clearing session");
        useAuthStore.getState().clearUser();
      } else {
        // Network error, 404, 5xx — don't clear the session. The app will
        // start without an access token and the 401 interceptor will retry
        // individual requests if the backend enforces authentication.
        logger.warn(
          "Could not pre-refresh session, continuing without access token",
          { status },
        );
      }
    }
  }

  logger.info("Bootstrap complete");
}
